import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EvStationSpaceCard } from '../../../../core/models/cityparking/ev-station-monitor.model';
import { MapperEvStationMonitorService } from '../../../../core/mappers/cityparking/mapper-ev-station-monitor.service';
import { ApiEvStationMonitorService } from '../../../../core/services/api/cityparking/api-ev-station-monitor.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalEvChargerDetailComponent } from '../../modals/ev-charger/modal-ev-charger-detail/modal-ev-charger-detail.component';
import { ModalEvChargerLicenseEditComponent } from '../../modals/ev-charger/modal-ev-charger-license-edit/modal-ev-charger-license-edit.component';
import { ModalEvStationManualEntryComponent } from '../../modals/ev-charger/modal-ev-station-manual-entry/modal-ev-station-manual-entry.component';

@Component({
  selector: 'app-ev-station-monitor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    ModalEvChargerDetailComponent,
    ModalEvChargerLicenseEditComponent,
    ModalEvStationManualEntryComponent,
  ],
  templateUrl: './ev-station-monitor.component.html',
  styleUrl: './ev-station-monitor.component.css',
})
export class EvStationMonitorComponent implements OnInit, OnDestroy {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiEvStationMonitorService);
  private readonly mapper = inject(MapperEvStationMonitorService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly router = inject(Router);

  private static readonly LIVE_INTERVAL_SEC = 60;

  cards = signal<EvStationSpaceCard[]>([]);
  isLoading = signal(false);
  lastFetchedAt = signal<Date | null>(null);

  enableLiveRefresh = false;
  liveCountdownSec = 0;
  private liveCountdownTimer: ReturnType<typeof setInterval> | null = null;

  isDetailModalOpen = false;
  selectedTransaction: {
    id: number;
    historyUuid: string;
    licensePlate: string;
    siteEv: string;
    spaceCode: string;
  } | null = null;

  isLicenseEditModalOpen = false;
  selectedLicenseEditData: {
    historyUid: string;
    licensePlate: string;
    siteEvName: string;
    spaceName: string;
    userId: number;
    imageSrc?: string | null;
    transactionEvId?: number;
  } | null = null;

  isManualEntryModalOpen = false;
  selectedManualEntryGate: { gateId: number; gateName: string } | null = null;

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => {
        this.loadCards();
        if (this.enableLiveRefresh) {
          this.resetLiveCountdown();
        }
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get userId(): number {
    const raw = this.cookieService.get('user_id');
    return raw ? parseInt(raw, 10) : 0;
  }

  get menuId(): number {
    return this.permissionService.getMenuId(this.router.url) ?? 0;
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get occupiedCount(): number {
    return this.cards().filter((c) => c.isFull).length;
  }

  get emptyCount(): number {
    return this.cards().filter((c) => !c.isFull).length;
  }

  get chargerIdleCount(): number {
    return this.cards().filter((c) => c.relayChargerStatus === 'idle').length;
  }

  get chargerTakenCount(): number {
    return this.cards().filter((c) => this.isActivelyCharging(c)).length;
  }

  get chargerErrorCount(): number {
    return this.cards().filter((c) =>
      this.mapper.isTrueOfflineError(c.relayChargerStatus, c.relayVendorErrorCode),
    ).length;
  }

  get chargerCompletedCount(): number {
    return this.cards().filter((c) =>
      c.relayChargerStatus === 'error'
      && this.mapper.isChargeCompletedVendorCode(c.relayVendorErrorCode),
    ).length;
  }

  cardAccentClass(card: EvStationSpaceCard): string {
    if (this.mapper.isChargeCompletedVendorCode(card.relayVendorErrorCode)
      && card.relayChargerStatus === 'error') {
      return 'ev-card--empty';
    }
    if (this.mapper.isTrueOfflineError(card.relayChargerStatus, card.relayVendorErrorCode)) {
      return 'ev-card--error';
    }
    if (this.isActivelyCharging(card)) {
      return 'ev-card--taken';
    }
    if (card.isFull) {
      return 'ev-card--occupied';
    }
    return 'ev-card--empty';
  }

  ngOnInit(): void {
    if (this.selectedSiteId) {
      this.loadCards();
    }
  }

  ngOnDestroy(): void {
    this.stopLiveRefresh();
  }

  onRefresh(): void {
    this.loadCards();
    if (this.enableLiveRefresh) {
      this.resetLiveCountdown();
    }
  }

  onLiveRefreshToggle(): void {
    if (this.enableLiveRefresh) {
      this.startLiveRefresh();
    } else {
      this.stopLiveRefresh();
    }
  }

  get liveCountdownLabel(): string {
    const sec = Math.max(0, this.liveCountdownSec);
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  private startLiveRefresh(): void {
    this.stopLiveRefresh();
    this.resetLiveCountdown();
    this.liveCountdownTimer = setInterval(() => {
      if (!this.enableLiveRefresh) {
        this.stopLiveRefresh();
        return;
      }
      this.liveCountdownSec -= 1;
      if (this.liveCountdownSec <= 0) {
        this.loadCards(true);
        this.resetLiveCountdown();
      }
    }, 1000);
  }

  private stopLiveRefresh(): void {
    if (this.liveCountdownTimer) {
      clearInterval(this.liveCountdownTimer);
      this.liveCountdownTimer = null;
    }
    this.liveCountdownSec = 0;
  }

  private resetLiveCountdown(): void {
    this.liveCountdownSec = EvStationMonitorComponent.LIVE_INTERVAL_SEC;
  }

  trackByGateId(_: number, item: EvStationSpaceCard): number {
    return item.gateId;
  }

  imageSrc(card: EvStationSpaceCard): string | null {
    return this.mapper.toImageSrc(card.imageBase64);
  }

  transactionLabel(card: EvStationSpaceCard): string {
    if (!card.isFull) return 'ไม่มี History UID';
    const uid = card.historyUid?.trim();
    return uid ? `History UID: ${uid}` : 'ไม่มี History UID';
  }

  ownerStatusLabel(card: EvStationSpaceCard): string {
    if (!card.isFull) return '';
    return card.activate || !!card.lineUid?.trim() ? 'มีเจ้าของ' : 'ไม่มีเจ้าของ';
  }

  chargingStatusClass(card: EvStationSpaceCard): string {
    return this.mapper.chargingStatusClass(card.chargingStatus);
  }

  chargingStatusLabel(card: EvStationSpaceCard): string {
    return this.mapper.chargingStatusLabelTh(card.chargingStatus, card.chargingStatusLabel);
  }

  relayChargerStatusClass(card: EvStationSpaceCard): string {
    return this.mapper.relayChargerStatusClass(
      card.relayChargerStatus,
      card.relayVendorErrorCode,
      card.chargingStatus,
    );
  }

  relayChargerStatusLabel(card: EvStationSpaceCard): string {
    return this.mapper.relayChargerStatusLabelTh(
      card.relayChargerStatus,
      card.relayVendorErrorCode,
      card.chargingStatus,
    );
  }

  /** True "กำลังชาร์จ": relay taken + chargingStatus === charging */
  isActivelyCharging(card: EvStationSpaceCard): boolean {
    return this.mapper.isActivelyCharging(card.relayChargerStatus, card.chargingStatus);
  }

  isChargeCompleted(card: EvStationSpaceCard): boolean {
    return card.relayChargerStatus === 'error'
      && this.mapper.isChargeCompletedVendorCode(card.relayVendorErrorCode);
  }

  isTrueOffline(card: EvStationSpaceCard): boolean {
    return this.mapper.isTrueOfflineError(card.relayChargerStatus, card.relayVendorErrorCode);
  }

  canCreateManualEntry(card: EvStationSpaceCard): boolean {
    return this.canCreate && this.mapper.canCreateManualEntry(card);
  }

  openManualEntryModal(card: EvStationSpaceCard, event: Event): void {
    event.stopPropagation();
    if (!this.canCreateManualEntry(card)) {
      this.toast.error('ช่องนี้ยังไม่พร้อมทำรายการเข้า');
      return;
    }
    if (!this.userId || !this.menuId) {
      this.toast.error('ไม่พบสิทธิ์เมนูหรือผู้ใช้');
      return;
    }
    this.selectedManualEntryGate = {
      gateId: card.gateId,
      gateName: card.gateName,
    };
    this.isManualEntryModalOpen = true;
  }

  closeManualEntryModal(): void {
    this.isManualEntryModalOpen = false;
    this.selectedManualEntryGate = null;
  }

  onManualEntrySuccess(): void {
    this.loadCards();
  }

  onCardClick(card: EvStationSpaceCard): void {
    if (!card.isFull || card.transactionId == null) return;
    this.openDetailModal(card);
  }

  openDetailModal(card: EvStationSpaceCard): void {
    if (!card.isFull || card.transactionId == null) return;
    this.selectedTransaction = {
      id: card.transactionId,
      historyUuid: card.historyUid,
      licensePlate: card.licensePlate,
      siteEv: card.siteEv,
      spaceCode: card.spaceCode || card.deviceKey,
    };
    this.isDetailModalOpen = true;
  }

  closeDetailModal(): void {
    this.isDetailModalOpen = false;
    this.selectedTransaction = null;
  }

  openLicenseEditModal(card: EvStationSpaceCard, event: Event): void {
    event.stopPropagation();
    if (!card.isFull) return;
    if (!card.historyUid) {
      this.toast.error('ไม่พบ History UID สำหรับแก้ไขทะเบียน');
      return;
    }
    if (!this.userId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้');
      return;
    }

    this.selectedLicenseEditData = {
      historyUid: card.historyUid,
      licensePlate: card.licensePlate,
      siteEvName: card.siteEv,
      spaceName: card.spaceCode || card.deviceKey,
      userId: this.userId,
      imageSrc: this.imageSrc(card),
      transactionEvId: card.transactionId ?? undefined,
    };
    this.isLicenseEditModalOpen = true;
  }

  closeLicenseEditModal(): void {
    this.isLicenseEditModalOpen = false;
    this.selectedLicenseEditData = null;
  }

  onLicenseSaved(_newLicensePlate: string): void {
    this.toast.success('บันทึกทะเบียนรถเรียบร้อย');
    this.loadCards();
  }

  private loadCards(isSilent = false): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.cards.set([]);
      return;
    }

    if (!isSilent) {
      this.isLoading.set(true);
    }
    forkJoin({
      cards: this.api.getEvStationSpaceCards(siteId),
      relay: this.api.getEvSpaceChargerStatus(siteId).pipe(
        catchError((err) => {
          console.error(err);
          return of(null);
        }),
      ),
    }).subscribe({
      next: ({ cards, relay }) => {
        console.log('cards', cards);
        const list = Array.isArray(cards) ? cards.map((d) => this.mapper.mapFromDto(d)) : [];
        const relayMapped = relay ? this.mapper.mapSpaceChargerStatusDto(relay) : null;
        this.cards.set(this.mapper.mergeRelayStatusOntoCards(list, relayMapped));
        this.lastFetchedAt.set(new Date());
        if (!isSilent) {
          this.isLoading.set(false);
        }
      },
      error: (err) => {
        console.error(err);
        if (!isSilent) {
          this.toast.error('ไม่สามารถโหลดข้อมูลมอนิเตอร์สถานี EV ได้');
          this.isLoading.set(false);
        }
      },
    });
  }
}
